<template>
  <section class="wiki-page">
    <div class="wiki-hero" role="banner">
      <div>
        <h1>ويكي المؤسسة</h1>
        <p>سجل موحد للإنجازات والأحداث عبر جميع شركاتنا.</p>
      </div>
      <div class="wiki-actions">
        <WikiSearchBar @select="handleSuggestion" @search="handleSearch" />
        <Button
          v-if="auth.isAdminRole"
          label="مقال جديد"
          icon="pi pi-plus"
          @click="createNew"
        />
      </div>
    </div>

    <div class="filters">
      <div class="tags">
        <span class="label">الوسوم الشائعة:</span>
        <div v-if="tags.length" class="tag-wrap">
          <div v-for="tag in tags" :key="tag.tag_id" class="tag-chip">
            <Button
              class="tag-btn"
              size="small"
              severity="secondary"
              outlined
              @click="applyTag(tag)"
            >
              #{{ tag.name }}
              <small v-if="typeof tag.usage_count === 'number'">({{ tag.usage_count }})</small>
            </Button>
            <button
              v-if="auth.isAdminRole"
              class="tag-delete"
              :disabled="tagDeleting === tag.slug"
              @click.stop="removeTag(tag)"
              :aria-label="`حذف الوسم ${tag.name}`"
            >
              <i class="pi pi-times" />
            </button>
          </div>
        </div>
        <span v-else class="text-muted">لا توجد وسوم مرتبطة بمقالات حالياً.</span>
        <Button
          v-if="route.query.tag || route.query.employeeId || route.query.q"
          text
          size="small"
          @click="resetFilters"
        >
          مسح التصفية
        </Button>
      </div>
    </div>

    <div v-if="loading" class="text-center text-muted py-6">...جار التحميل</div>

    <div v-else>
      <div v-if="!pages.length" class="text-muted py-6">
        لا توجد مقالات مطابقة.
      </div>
      <div class="page-grid">
        <article v-for="page in pages" :key="page.page_id" class="wiki-card">
          <RouterLink :to="`/wiki/${page.slug}`" class="title">
            {{ page.title }}
          </RouterLink>
          <p class="summary">
            {{ page.summary || 'لا يوجد ملخص' }}
          </p>
          <div class="meta">
            <span v-if="page.event_date">📅 {{ formatDate(page.event_date) }}</span>
            <span class="text-xs text-muted">آخر تحديث {{ formatDateTime(page.updated_at) }}</span>
          </div>
          <div class="tag-list">
            <Tag
              v-for="tag in page.tags"
              :key="tag.tag_id ?? `${tag.entity_type || 'custom'}-${tag.entity_id ?? tag.slug ?? tag.label}`"
              severity="info"
              rounded
              class="tag-pill"
              :style="{ cursor: tag.slug ? 'pointer' : 'default' }"
              @click="tag.slug && navigateToTag(tag.slug)"
            >
              #{{ tag.label }}
              <small v-if="tag.entity_type && tag.entity_type !== 'tag'" class="tag-type">({{ formatTagType(tag.entity_type) }})</small>
            </Tag>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import { useToast } from 'primevue/usetoast'
import WikiSearchBar from '@/components/wiki/WikiSearchBar.vue'
import { deleteWikiTag, fetchWikiPages, fetchWikiTags, type WikiPageSummary, type WikiTagLink, type TagRecord } from '@/services/wiki'
import { useAuthStore } from '@/store/auth'

const auth = useAuthStore()
const route = useRoute()
const router = useRouter()
const toast = useToast()

const pages = ref<WikiPageSummary[]>([])
const tags = ref<TagRecord[]>([])
const loading = ref(false)
const tagDeleting = ref<string | null>(null)

onMounted(async () => {
  await Promise.all([loadPages(), loadTags()])
})

watch(
  () => route.query,
  () => loadPages(),
  { deep: true }
)

async function loadPages() {
  loading.value = true
  try {
    const data = await fetchWikiPages({
      search: (route.query.q as string) || undefined,
      tag: (route.query.tag as string) || undefined,
      employee_id: route.query.employeeId ? Number(route.query.employeeId) : undefined,
    })
    pages.value = data.items
  } finally {
    loading.value = false
  }
}

async function loadTags() {
  try {
    tags.value = await fetchWikiTags({ limit: 100 })
  } catch (err) {
    console.error('Failed to load tags', err)
  }
}

function formatDate(value?: string | null) {
  if (!value) return 'غير محدد'
  const date = new Date(value)
  return date.toLocaleDateString('ar-EG')
}

function formatDateTime(value: string) {
  const date = new Date(value)
  return date.toLocaleDateString('ar-EG')
}

function createNew() {
  router.push('/wiki/new')
}

function applyTag(tag: TagRecord) {
  router.push({ path: `/tags/${tag.slug}` })
}

function navigateToTag(slug?: string | null) {
  if (!slug) return
  router.push({ path: `/tags/${slug}` })
}

function formatTagType(type?: string | null) {
  if (!type) return ''
  const map: Record<string, string> = {
    company: 'شركة',
    department: 'قسم',
    rank: 'رتبة',
    office: 'مكتب',
    title: 'منصب',
    hierarchy: 'هيكل',
    city: 'مدينة',
    state: 'ولاية',
    country: 'دولة',
  }
  return map[type] || type
}

function resetFilters() {
  router.push({ path: '/wiki' })
}

function handleSuggestion(option: { type: string; value: any }) {
  if (!option) return
  switch (option.type) {
    case 'article':
      router.push(`/wiki/${option.value.slug}`)
      break
    case 'tag':
      router.push({ path: `/tags/${option.value.slug}` })
      break
    case 'employee':
      router.push({ path: '/wiki', query: { employeeId: option.value.employee_id } })
      break
    default:
      break
  }
}

function handleSearch(text?: string) {
  if (!text) return
  router.replace({ path: '/wiki', query: { q: text } })
}

async function removeTag(tag: TagRecord) {
  if (!auth.isAdminRole) return
  if (!tag.slug) {
    toast.add({ severity: 'warn', summary: 'لا يمكن حذف هذا الوسم', life: 2500 })
    return
  }
  const confirmed = window.confirm(`سيتم حذف الوسم #${tag.name} من جميع المقالات. هل تريد المتابعة؟`)
  if (!confirmed) return
  tagDeleting.value = tag.slug
  try {
    await deleteWikiTag(tag.slug)
    toast.add({ severity: 'success', summary: 'تم حذف الوسم', detail: `#${tag.name}`, life: 2500 })
    await Promise.all([loadTags(), loadPages()])
  } catch (err: any) {
    console.error('Failed to delete tag', err)
    toast.add({
      severity: 'error',
      summary: 'تعذر حذف الوسم',
      detail: err?.response?.data?.detail || 'حدث خلل أثناء الحذف',
      life: 4000,
    })
  } finally {
    tagDeleting.value = null
  }
}
</script>

<style scoped>
.wiki-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: 5rem 1rem 2rem;
}

@media (max-width: 768px) {
  .wiki-page {
    padding-top: 4rem;
  }
}

.wiki-hero {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #f8fafc, #ffffff);
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 15px 35px rgba(15, 23, 42, 0.08);
}

.wiki-hero h1 {
  margin: 0 0 0.25rem;
  color: #0f172a;
}

.wiki-hero p {
  margin: 0;
  color: #475569;
}

@media (min-width: 768px) {
  .wiki-hero {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.wiki-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.filters {
  margin-bottom: 1rem;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.5rem;
}

.tags .label {
  font-weight: 600;
}

.tag-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.tag-btn {
  font-size: 0.85rem;
  border-radius: 2rem;
}

.tag-delete {
  border: none;
  background: transparent;
  color: #b91c1c;
  cursor: pointer;
  padding: 0.1rem;
  border-radius: 999px;
  transition: color 0.2s;
}

.tag-delete:hover:not(:disabled) {
  color: #7f1d1d;
}

.tag-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.wiki-card {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: #fff;
}

.wiki-card .title {
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  color: #0f172a;
}

.wiki-card .title:hover {
  color: #2563eb;
}

.summary {
  color: #475569;
  font-size: 0.95rem;
  min-height: 48px;
}

.meta {
  font-size: 0.85rem;
  color: #475569;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag-pill {
  cursor: pointer;
}

.tag-type {
  margin-right: 0.25rem;
  font-size: 0.7rem;
  opacity: 0.7;
}
</style>
